import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { APIClient, APIEndpoint } from '../../lib/api_client';
import { CircularProgress, Typography, Container, Paper, Box, Alert, Card, CardContent, Chip, Avatar, Grid, IconButton, Dialog, DialogTitle, DialogContent, Divider } from '@mui/material';
import { Public as PublicIcon, Lock as LockIcon, Person as PersonIcon, Share as ShareIcon, Edit as EditIcon, ManageAccounts as ManageAccountsIcon, Close as CloseIcon, LocationOn as LocationOnIcon, CheckCircle as CheckCircleIcon, HelpOutline as HelpOutlineIcon, Cancel as CancelIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useAuth } from '../../hooks/AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePlan } from '../../hooks/PlanProvider';
import { useSnackbar } from '../../hooks/SnackbarProvider';
import { CustomPlace, CustomPhoto, getLng, getLat } from '../../types/interfaces';
import SharePlanDialog from '../PlanBuilder/SharePlanDialog';
import BaseMap from '../Map/BaseMap';
import Path from '../Map/Path';
import { MarkerF } from '@react-google-maps/api';
import { useMaps } from '../../hooks/MapProvider';
import { Button } from '../Button/Button';
import { Register } from '../Register/Register';
import './PlanPage.scss';
import { SocialAnalytics } from '../../lib/analytics';

// Define the expected shape of an Activity within a Plan
interface Activity {
  placeId: string; // Expect camelCase from API client
  name: string;
  lat: number;
  lng: number;
  photos?: CustomPhoto[];
  // Add other activity fields if present, e.g., startTime, endTime
}

// Define the expected shape of the Plan object within the API response
interface PlanData {
  id: string; // Or number, adjust as per actual API response and usage
  name: string;
  owner?: number; // Original owner ID, might still be useful internally
  ownerUsername?: string; // Displayable owner name
  activities: Activity[];
  createdAt: string;
  updatedAt: string;
  isPublic: boolean;
  slug: string;
  shareUrl: string;
}

// Define the expected shape of the API response for a shared plan
interface PlanResponseData {
  plan: PlanData;
  viewerState: "guest" | "owner" | "participant" | "invitee"; // camelCase
  rsvpStatus: "going" | "maybe" | "no" | null; // camelCase
}

const PlanPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const apiClient = APIClient;
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSelectedPlan } = usePlan(); // Get setSelectedPlan
  const { triggerSnackbar } = useSnackbar(); // Get snackbar function
  const { mapsClient } = useMaps(); // Get maps client for directions
  const [shareDialogOpen, setShareDialogOpen] = useState(false); // For SharePlanDialog
  const [signupDialogOpen, setSignupDialogOpen] = useState(false); // For signup dialog

  const { data, isLoading, error, refetch } = useQuery<PlanResponseData, Error, PlanResponseData, readonly [string, string | undefined]>({
    queryKey: ['sharedPlan', slug],
    queryFn: async () => {
      if (!slug) throw new Error('Plan slug is missing');
      return apiClient.get<PlanResponseData>(`${APIEndpoint}/plans/${slug}/`).then(res => {
        // The APIClient interceptor should handle keysToCamel conversion.
        // If not, you might need to manually convert here, e.g., using a utility function.
        // For now, assuming data is correctly camelCased by the interceptor.
        if (!res.data.plan || !res.data.plan.activities) {
          // Handle cases where plan or activities might be unexpectedly null/undefined
          // to prevent runtime errors, though API should ideally always return these.
          // This could be due to an error state not caught by the query's error handling.
          console.error("Plan data or activities missing in response:", res.data);
          throw new Error("Received incomplete plan data from server.");
        }
        return res.data;
      });
    },
    enabled: !!slug, // Only run query if slug is available
    // Consider retry logic or stale time as needed
  });

  // Calculate map settings when data is available
  const mapSettings = React.useMemo(() => {
    if (!data?.plan?.activities || data.plan.activities.length === 0) {
      return { center: { lat: 37.7749, lng: -122.4194 }, zoom: 13 };
    }

    if (data.plan.activities.length === 1) {
      return {
        center: { lat: getLat(data.plan.activities[0]), lng: getLng(data.plan.activities[0]) },
        zoom: 13
      };
    }

    // For multiple activities, calculate bounds - but only if Google Maps is loaded
    if (typeof google !== 'undefined' && google.maps && google.maps.LatLngBounds) {
      const bounds = new google.maps.LatLngBounds();
      data.plan.activities.forEach(activity => {
        bounds.extend({ lat: getLat(activity), lng: getLng(activity) });
      });

      const center = bounds.getCenter();
      return {
        center: { lat: center.lat(), lng: center.lng() },
        zoom: 13 // Let the map auto-adjust zoom based on bounds
      };
    } else {
      // Fallback: calculate center manually if Google Maps isn't loaded yet
      const totalLat = data.plan.activities.reduce((sum, activity) => sum + activity.lat, 0);
      const totalLng = data.plan.activities.reduce((sum, activity) => sum + activity.lng, 0);
      const avgLat = totalLat / data.plan.activities.length;
      const avgLng = totalLng / data.plan.activities.length;

      return {
        center: { lat: avgLat, lng: avgLng },
        zoom: 13
      };
    }
  }, [data?.plan?.activities]);

  // Get the first activity's photo for header background
  const headerBackgroundImage = React.useMemo(() => {
    if (data?.plan?.activities && data.plan.activities.length > 0 &&
      data.plan.activities[0].photos && data.plan.activities[0].photos.length > 0) {
      const photo = data.plan.activities[0].photos[0];
      return photo.url;
    }
    return null;
  }, [data?.plan?.activities]);

  useEffect(() => {
    if (data?.plan) {
      // When plan data is successfully fetched, update the PlanProvider context.
      // Ensure the fetchedData.plan structure is compatible with SavedPlan type.
      // The PlanData interface should align with SavedPlan or be mappable.
      setSelectedPlan(data.plan as any); // Cast to 'any' for now, ideally align types
    }
  }, [data, setSelectedPlan]);

  // Handle RSVP action (example)
  const handleRSVP = async (rsvpStatus: 'going' | 'maybe' | 'no') => {
    if (!auth.isAuthenticated) {
      // Redirect to login, preserving current location to return to
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    if (!data?.plan?.id) return;

    try {
      // Use plan ID for authenticated RSVP operation
      await apiClient.post(`${APIEndpoint}/plans/${data.plan.id}/rsvp/`, { status: rsvpStatus });
      // Invalidate and refetch plan data to show updated RSVP status and potentially viewer_state
      refetch();

      // Track RSVP analytics
      SocialAnalytics.rsvp(data.plan.id, rsvpStatus);

      console.log(`RSVP status updated to ${rsvpStatus}`);
    } catch (err) {
      console.error("Failed to RSVP:", err);
      // Show an error message to the user
    }
  };

  // Handle joining a public plan
  const handleJoinPlan = async () => {
    if (!auth.isAuthenticated) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    if (!data?.plan?.id) return;

    try {
      // Request to join the public plan
      await apiClient.post(`${APIEndpoint}/plans/${data.plan.id}/join/`);
      // Invalidate and refetch plan data to show updated viewer state
      refetch();
      triggerSnackbar('Successfully joined the plan! 🎉', 4000, 'success');
    } catch (err) {
      console.error("Failed to join plan:", err);
      triggerSnackbar('Failed to join the plan. Please try again.', 4000, 'error');
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">Error loading plan: {error.message}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">Plan not found or still loading...</Alert>;
  }

  const { plan, viewerState, rsvpStatus } = data; // Already camelCased by interceptor

  // Convert activities to CustomPlace format for MapMarker
  const customPlaces: CustomPlace[] = plan.activities.map((activity) => ({
    placeId: activity.placeId,
    name: activity.name,
    geometry: {
      location: new google.maps.LatLng(getLat(activity), getLng(activity))
    },
    photos: activity.photos || [],
    // Add optional fields with default values
    formatted_address: '',
    rating: 0,
    price_level: 0,
    types: [],
    vicinity: ''
  }));

  const handleEditPlan = () => {
    if (plan) {
      // Ensure id is string if PlanProvider or builder expects it as such
      // The PlanData interface has id as string, adjust if it's number from API initially
      setSelectedPlan({ ...plan, id: String(plan.id) } as any); // Cast for now
      navigate('/builder');
    }
  };

  const handleManageInvites = () => {
    setShareDialogOpen(true);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          background: headerBackgroundImage
            ? `linear-gradient(135deg, rgba(102, 126, 234, 0.21) 0%, rgba(118, 75, 162, 0.8) 100%), url(${headerBackgroundImage})`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: 'white',
          borderRadius: 3,
          p: 4,
          mb: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {/* Add backdrop overlay for better text readability when there's a background image */}
          {headerBackgroundImage && (
            <Box
              sx={{
                position: 'absolute',
                top: -16,
                left: -16,
                right: -16,
                bottom: -16,
                borderRadius: 3,
                zIndex: -1
              }}
            />
          )}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {plan.name}
              </Typography>
              {plan.ownerUsername && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Avatar sx={{ width: 24, height: 24, bgcolor: 'rgba(255,255,255,0.2)' }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    {plan.ownerUsername}
                  </Typography>
                </Box>
              )}
              <Chip
                icon={plan.isPublic ? <PublicIcon /> : <LockIcon />}
                label={plan.isPublic ? 'Public Plan' : 'Private Plan'}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            bgcolor: headerBackgroundImage ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
            zIndex: 0
          }}
        />
      </Paper>

      <Grid container spacing={3}>
        {/* Map Section */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
            <Box sx={{ height: 400, position: 'relative' }}>
              {plan.activities && plan.activities.length > 0 ? (
                <BaseMap
                  center={mapSettings.center}
                  zoom={mapSettings.zoom}
                >
                  {/* Render numbered markers for each activity */}
                  {customPlaces.map((place, index) => (
                    <MarkerF
                      key={place.placeId}
                      position={{
                        lat: getLat(place) ?? 0,
                        lng: getLng(place) ?? 0
                      }}
                      title={place.name}
                      onClick={() => {
                        console.log(`Clicked on ${place.name}`);
                      }}
                      icon={{
                        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                          <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16 0C7.16 0 0 7.16 0 16C0 24.84 16 40 16 40S32 24.84 32 16C32 7.16 24.84 0 16 0Z" fill="#667eea"/>
                            <circle cx="16" cy="16" r="12" fill="white"/>
                            <text x="16" y="21" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="#667eea">${index + 1}</text>
                          </svg>
                        `)}`,
                        scaledSize: new google.maps.Size(32, 40),
                        anchor: new google.maps.Point(16, 40),
                      }}
                    />
                  ))}

                  {/* Render paths between consecutive activities */}
                  {plan.activities.length > 1 && mapsClient && plan.activities.map((activity, index) => {
                    if (index < plan.activities.length - 1) {
                      const nextActivity = plan.activities[index + 1];
                      return (
                        <Path
                          key={`path-${index}`}
                          originCoords={{ lat: getLat(activity), lng: getLng(activity) }}
                          destinationCoords={{ lat: getLat(nextActivity), lng: getLng(nextActivity) }}
                          mapsClient={mapsClient}
                          isActive={true}
                        />
                      );
                    }
                    return null;
                  })}
                </BaseMap>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                    color: 'text.secondary'
                  }}
                >
                  <Box textAlign="center">
                    <LocationOnIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                    <Typography variant="h6">No activities yet</Typography>
                    <Typography variant="body2">Add some places to see them on the map</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Card>

          {/* Activities Section */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Activities ({plan.activities?.length || 0})
              </Typography>

              {plan.activities && plan.activities.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  {plan.activities.map((activity: Activity, index: number) => (
                    <Box key={index} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                              {index + 1}
                            </Avatar>
                            <Box flex={1}>
                              <Typography variant="h6" fontWeight="medium">
                                {activity.name}
                              </Typography>
                              {getLat(activity) && getLng(activity) && (
                                <Typography variant="body2" color="text.secondary">
                                  {getLat(activity).toFixed(4)}, {getLng(activity).toFixed(4)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box textAlign="center" sx={{ py: 4 }}>
                  <LocationOnIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No activities planned yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This plan is waiting for some exciting activities to be added.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* User Status Card */}
          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Your Status
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Chip
                  label={viewerState === 'owner' ? 'Plan Owner' :
                    viewerState === 'participant' ? 'Participant' :
                      viewerState === 'invitee' ? 'Invited' : 'Guest'}
                  color={viewerState === 'owner' ? 'primary' :
                    viewerState === 'participant' ? 'success' :
                      viewerState === 'invitee' ? 'warning' : 'default'}
                  sx={{ mr: 1 }}
                />
                {auth.isAuthenticated && rsvpStatus && (
                  <Chip
                    icon={rsvpStatus === 'going' ? <CheckCircleIcon /> :
                      rsvpStatus === 'maybe' ? <HelpOutlineIcon /> :
                        <CancelIcon />}
                    label={rsvpStatus === 'going' ? 'Going' :
                      rsvpStatus === 'maybe' ? 'Maybe' :
                        'Not Going'}
                    color={rsvpStatus === 'going' ? 'success' :
                      rsvpStatus === 'maybe' ? 'warning' : 'error'}
                    size="small"
                  />
                )}
              </Box>

              {/* Action Buttons based on viewer state */}
              {viewerState === 'guest' && !auth.isAuthenticated && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    intent="primary"
                    size="medium"
                    onClick={() => setSignupDialogOpen(true)}
                    sx={{ width: '100%', mb: 1 }}
                  >
                    Sign Up to Join
                  </Button>
                  <Button
                    intent="secondary"
                    size="medium"
                    onClick={() => navigate('/login', { state: { from: location.pathname } })}
                    sx={{ width: '100%' }}
                  >
                    Log In
                  </Button>
                </Box>
              )}

              {/* Authenticated user viewing public plan who isn't already involved */}
              {viewerState === 'guest' && auth.isAuthenticated && plan.isPublic && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    intent="primary"
                    size="medium"
                    onClick={handleJoinPlan}
                    sx={{ width: '100%' }}
                  >
                    <PersonAddIcon sx={{ mr: 1, fontSize: 20 }} />
                    Join Plan
                  </Button>
                </Box>
              )}

              {(viewerState === 'invitee' || (viewerState === 'participant' && rsvpStatus !== 'going')) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Will you be joining this plan?
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                      intent="success"
                      size="small"
                      onClick={() => handleRSVP('going')}
                      sx={{ width: '100%' }}
                    >
                      Going
                    </Button>
                    <Button
                      intent="warning"
                      size="small"
                      onClick={() => handleRSVP('maybe')}
                      sx={{ width: '100%' }}
                    >
                      Maybe
                    </Button>
                    <Button
                      intent="danger"
                      size="small"
                      onClick={() => handleRSVP('no')}
                      sx={{ width: '100%' }}
                    >
                      Can't Go
                    </Button>
                  </Box>
                </Box>
              )}

              {viewerState === 'participant' && rsvpStatus === 'going' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  You're going to this plan! 🎉
                </Alert>
              )}

              {viewerState === 'owner' && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You own this plan
                  </Alert>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                      intent="primary"
                      size="medium"
                      onClick={handleEditPlan}
                      sx={{ width: '100%' }}
                    >
                      <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                      Edit Plan
                    </Button>
                    <Button
                      intent="secondary"
                      size="medium"
                      onClick={handleManageInvites}
                      sx={{ width: '100%' }}
                    >
                      <ManageAccountsIcon sx={{ mr: 1, fontSize: 20 }} />
                      Manage Invites
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Plan Details Card */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Plan Details
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Created: {new Date(plan.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Updated: {new Date(plan.updatedAt).toLocaleDateString()}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Share this plan:
                  </Typography>
                  <Button
                    intent="ghost"
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(plan.shareUrl);
                      triggerSnackbar('Link copied to clipboard!', 3000, 'success');
                    }}
                    sx={{ width: '100%', justifyContent: 'flex-start' }}
                  >
                    <ShareIcon sx={{ mr: 1, fontSize: 16 }} />
                    Copy Link
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Signup Dialog */}
      <Dialog
        open={signupDialogOpen}
        onClose={() => setSignupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Join to Access This Plan</Typography>
            <IconButton onClick={() => setSignupDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Register />
        </DialogContent>
      </Dialog>

      {/* SharePlanDialog for owners */}
      {plan && viewerState === 'owner' && (
        <SharePlanDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
        />
      )}
    </Container>
  );
};

export default PlanPage; 